import React from "react";
import { Box, Card, Typography } from "@mui/material";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

const MonthlyChart = ({ data, year = new Date().getFullYear() }) => {
  const hasData = Array.isArray(data) && data.length > 0;
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: "#0f172a",
            padding: "12px 16px",
            borderRadius: "12px",
            boxShadow: "0 12px 32px rgba(0,0,0,0.25)",
            border: "1px solid rgba(255,255,255,0.06)",
            minWidth: "140px",
          }}
        >
          <Typography sx={{ fontWeight: 700, color: "#e2e8f0", fontSize: "0.72rem", mb: 0.8, letterSpacing: "0.02em" }}>
            {label} {year}
          </Typography>
          {payload.map((entry, index) => (
            <Box key={index} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mt: 0.4 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                <Box sx={{ width: 6, height: 6, borderRadius: entry.dataKey === "promedio" ? "50%" : "1.5px", backgroundColor: entry.color }} />
                <Typography sx={{ color: "#94a3b8", fontSize: "0.68rem" }}>
                  {entry.name}
                </Typography>
              </Box>
              <Typography sx={{ color: "#fff", fontSize: "0.72rem", fontWeight: 700 }}>
                {entry.value.toLocaleString()}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: "18px",
        border: "1px solid rgba(0,0,0,0.04)",
        padding: "22px 24px 16px",
        backgroundColor: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.02), 0 6px 16px rgba(0,0,0,0.02)",
        width: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#0f172a", letterSpacing: "-0.01em" }}>
            Egresos mensuales
          </Typography>
          <Typography sx={{ fontSize: "0.65rem", color: "#94a3b8", mt: 0.2 }}>
            Resumen del año {year}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
            <Box sx={{ width: 10, height: 4, borderRadius: "2px", background: "linear-gradient(90deg, #6366f1, #818cf8)" }} />
            <Typography sx={{ fontSize: "0.62rem", color: "#94a3b8", fontWeight: 500 }}>Cantidad</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", border: "2px solid #f59e0b", boxSizing: "border-box" }} />
            <Typography sx={{ fontSize: "0.62rem", color: "#94a3b8", fontWeight: 500 }}>Promedio</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1, width: "100%", minHeight: "270px" }}>
        {!hasData ? (
          <Box
            sx={{
              height: "100%",
              minHeight: "270px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#64748b" }}>
              Sin movimientos este año
            </Typography>
            <Typography sx={{ fontSize: "0.68rem", color: "#94a3b8" }}>
              Cuando registres ingresos o egresos vas a verlos acá.
            </Typography>
          </Box>
        ) : (
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={data}
            margin={{ top: 5, right: 5, left: -15, bottom: 0 }}
          >
            <defs>
              <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6366f1" stopOpacity={1} />
                <stop offset="100%" stopColor="#818cf8" stopOpacity={0.6} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="0"
              stroke="#f5f5f7"
              vertical={false}
            />
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10.5, fill: "#a0aec0", fontWeight: 500 }}
              dy={10}
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "#c4c9d2" }}
              width={42}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "#c4c9d2" }}
              width={28}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(99, 102, 241, 0.03)" }}
            />
            <Bar
              yAxisId="left"
              dataKey="cantidad"
              fill="url(#barGrad)"
              radius={[8, 8, 0, 0]}
              name="Cantidad"
              maxBarSize={22}
            />
            <Line
              yAxisId="right"
              dataKey="promedio"
              stroke="#f59e0b"
              strokeWidth={2.5}
              dot={{ r: 3.5, fill: "#fff", stroke: "#f59e0b", strokeWidth: 2.5 }}
              activeDot={{ r: 5, fill: "#f59e0b", stroke: "#fff", strokeWidth: 2.5 }}
              name="Promedio"
              type="monotone"
            />
          </ComposedChart>
        </ResponsiveContainer>
        )}
      </Box>
    </Card>
  );
};

export default MonthlyChart;
